import useAsync from "@/hooks/useAsync";
import {
  Severity
} from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import * as React from "react";
import useApi from "@/hooks/useApi";
import { SiteSettings } from "./types";
import { TimeZone } from "@/types";
import useToastAlert from "@/hooks/useToastAlert";
import { SiteContext } from "@/context/SiteContext";
import { Site } from "@/types";
import GeneralSettings from "./components/GeneralSettings";
import UserAccessSettings from "./components/UserAccessSettings";
import AccountSecuritySettings from "./components/AccountSecuritySettings";
import ManageDataPrivacySettings from "./components/ManageDataPrivacySettings";
import TranscriptPurgingSettings from "./components/TranscriptPurgingSettings";
import SupportAccessSettings from "./components/SupportAccessSettings";
import AISettings from "./components/AISettings";
import AIProviderSettings from "./AIProviderSettings";

export default function Index() {
  const api = useApi();
  const { displayToast } = useToastAlert();
  const [searchFilter, setSearchFilter] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState("general");
  const [primaryLanguage, setPrimaryLanguage] = React.useState("en");
  const { site: contextSite } = React.useContext(SiteContext);
  const [site, setSite] = React.useState<Site>(contextSite);

  // Update local state when context changes (after SiteProvider loads)
  React.useEffect(() => {
    setSite(contextSite);
  }, [contextSite]);

  // Reload site data function for after save
  const reloadSiteData = async () => {
    try {
      // Reload site data (company info) from velaro-admin
      const siteResponse = await api.admin.get("Site");
      const siteData = await siteResponse.json();
      setSite(siteData);

      // Reload toggle data from velaro-messaging
      const settingsResponse = await api.messaging.get("SiteSettings");
      const settingsData = await settingsResponse.json();
      setSiteSettingsAsync(settingsData);

      return { site: siteData, settings: settingsData };
    } catch (error) {
      console.error("Failed to reload site data:", error);
    }
  };

  React.useEffect(() => {
    const handleSearch = (event: CustomEvent) => {
      setSearchFilter(event.detail.toLowerCase());
    };
    window.addEventListener("accountSearch", handleSearch as EventListener);
    return () =>
      window.removeEventListener(
        "accountSearch",
        handleSearch as EventListener
      );
  }, []);

  const {
    pending: loadingSiteSettings,
    value: siteSettings,
    setValue: setSiteSettingsAsync
  } = useAsync<SiteSettings>({
    immediate: true,
    asyncFunction: async () => {
      const response = await api.messaging.get("SiteSettings");
      return await response.json();
    }
  });

  // Create a wrapper function that handles the null case
  const setSiteSettings = React.useCallback(
    (updater: React.SetStateAction<SiteSettings>) => {
      setSiteSettingsAsync((prevValue) => {
        if (!prevValue) return prevValue;

        if (typeof updater === "function") {
          return updater(prevValue);
        }
        return updater;
      });
    },
    [setSiteSettingsAsync]
  );

  const { pending: loadingTimezones, value: timeZones } = useAsync<TimeZone[]>({
    immediate: true,
    asyncFunction: async () => {
      const response = await api.messaging.get("Timezones");
      return await response.json();
    }
  });

  const { pending: loadingCountries, value: countries } = useAsync<
    { countryID: string; countryName: string }[]
  >({
    immediate: true,
    asyncFunction: async () => {
      const response = await api.admin.get("Countries");
      return await response.json();
    }
  });

  async function save() {
    try {
      // Save site data (company info) to velaro-admin
      const response = await api.admin.post("Site", site);

      // Save siteSettings data (toggles) to velaro-messaging
      const siteSettingsResponse = await api.messaging.post("SiteSettings", siteSettings);

      if (response.ok && siteSettingsResponse.ok) {
        displayToast(Severity.Success, "Account settings saved successfully");
        await reloadSiteData();
      } else {
        displayToast(Severity.Error, "Failed to save account settings");
      }
    } catch (error) {
      displayToast(Severity.Error, "Failed to save account settings");
    }
  }

  const timeZoneOptions: Record<string, string> = {};
  timeZones?.forEach((tz) => {
    timeZoneOptions[tz.id] = tz.displayName;
  });

  const countryOptions: Record<string, string> = {};

  countries?.forEach((country) => {
    countryOptions[country.countryID] = country.countryName;
  });

  if (
    loadingSiteSettings ||
    loadingTimezones ||
    loadingCountries ||
    !siteSettings
  ) {
    return <LoadingSpinner />;
  }

  const isVisible = (text: string) => {
    if (!searchFilter) return true;
    return text.toLowerCase().includes(searchFilter);
  };

  return (
    <>
      <div>
        {/* Page header */}
        <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-slate-200 bg-white sticky top-0 z-10">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-slate-900 tracking-tight">Account Settings</h1>
            <p className="text-xs text-slate-400 mt-0.5">Manage your site configuration, AI settings, and access controls</p>
          </div>
          <button
            onClick={save}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
          >
            Save
          </button>
        </div>

        {/* Tab bar */}
        <div className="px-6 border-b border-slate-100 bg-white flex items-center gap-0 sticky top-[73px] z-10">
          {([
            ["general", "General"],
            ["user-access", "User Access"],
            ["ai-settings", "AI Settings"],
            ["ai-provider", "AI Provider"],
            ["manage-data-privacy", "Data Privacy"],
            ["transcript-purging", "Transcript Purging"],
            ["account-security", "Account Security"],
            ["support-access", "Support Access"]
          ] as const).map(([tab, label]) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                selectedTab === tab
                  ? "border-[var(--color-primary)] text-[var(--color-primary)]"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="px-6 py-5">
          {selectedTab === "general" && (
            <GeneralSettings
              site={site}
              setSite={setSite}
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              timeZoneOptions={timeZoneOptions}
              countryOptions={countryOptions}
              isVisible={isVisible}
            />
          )}
          {selectedTab === "user-access" && (
            <UserAccessSettings
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )}
          {selectedTab === "ai-settings" && (
            <AISettings
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
            />
          )}
          {selectedTab === "ai-provider" && (
            <AIProviderSettings />
          )}
          {selectedTab === "manage-data-privacy" && (
            <ManageDataPrivacySettings
              site={site}
              setSite={setSite}
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )}
          {selectedTab === "transcript-purging" && (
            <TranscriptPurgingSettings />
          )}
          {selectedTab === "account-security" && (
            <AccountSecuritySettings
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )}
          {selectedTab === "support-access" && (
            <SupportAccessSettings
              siteSettings={siteSettings}
              setSiteSettings={setSiteSettings}
              isVisible={isVisible}
            />
          )}
        </div>
      </div>
    </>
  );
}
